import React, { useState, useEffect, useMemo } from "react";
import api from "../services/api";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";

const COLORS = ["#6366f1", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6"];

// Custom hook to detect mobile/tablet screens for dynamic CSS styling
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return width;
}

function BroadcastNotification() {
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth <= 800;

  const [audience, setAudience] = useState("customers");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [showAsBanner, setShowAsBanner] = useState(false);
  const [bannerType, setBannerType] = useState("info");
  const [sending, setSending] = useState(false);

  // Active Banners & History State
  const [activeBanners, setActiveBanners] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Filter States
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const loadBanners = async () => {
    try {
      const res = await api.get("/broadcast/banners");
      setActiveBanners(res.data.banners || []);
    } catch (err) {
      console.error("Failed to load active banners", err);
    }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (typeFilter !== "all") params.type = typeFilter;

      const res = await api.get("/broadcast/history", { params });
      setBroadcasts(res.data.broadcasts || []);
    } catch (err) {
      console.error("Failed to load history", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadBanners();
  }, []);

  useEffect(() => {
    loadHistory();
  }, [startDate, endDate, typeFilter]);

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      alert("Title and message are required");
      return;
    }

    const bannerNote = showAsBanner ? " and shown as a persistent banner" : "";
    if (!window.confirm(`Send this notification to ALL ${audience}${bannerNote}?`)) return;

    try {
      setSending(true);
      await api.post("/broadcast/send", {
        audience,
        title: title.trim(),
        message: message.trim(),
        showAsBanner,
        bannerType,
      });
      alert("Broadcast sent successfully!");
      setTitle("");
      setMessage("");
      setShowAsBanner(false);
      loadBanners();
      loadHistory();
    } catch (err) {
      alert(err.response?.data?.message || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  const handleDeactivate = async (aud) => {
    if (!window.confirm(`Deactivate the active banner for ${aud}?`)) return;
    try {
      await api.post(`/broadcast/banner/${aud}/deactivate`);
      loadBanners();
    } catch (err) {
      alert("Failed to deactivate banner");
    }
  };

  // Analytics Computation
  const metrics = useMemo(() => {
    let totalSent = 0;
    let totalFailed = 0;
    let notificationOnlyCount = 0;
    let bannerAndNotificationCount = 0;

    broadcasts.forEach((b) => {
      totalSent += b.sentCount || 0;
      totalFailed += b.failedCount || 0;
      if (b.showAsBanner) bannerAndNotificationCount++;
      else notificationOnlyCount++;
    });

    return {
      totalBroadcasts: broadcasts.length,
      totalSent,
      totalFailed,
      notificationOnlyCount,
      bannerAndNotificationCount,
    };
  }, [broadcasts]);

  const pieData = useMemo(() => {
    return [
      { name: "Notification Only", value: metrics.notificationOnlyCount },
      { name: "Banner + Notification", value: metrics.bannerAndNotificationCount },
    ];
  }, [metrics]);

  const audienceData = useMemo(() => {
    const map = { customers: 0, drivers: 0, both: 0 };
    broadcasts.forEach((b) => {
      if (map[b.audience] !== undefined) map[b.audience]++;
    });
    return [
      { audience: "Customers", count: map.customers },
      { audience: "Drivers", count: map.drivers },
      { audience: "Both", count: map.both },
    ];
  }, [broadcasts]);

  const timelineData = useMemo(() => {
    const dailyMap = {};
    broadcasts.forEach((b) => {
      const dateStr = new Date(b.createdAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      if (!dailyMap[dateStr]) {
        dailyMap[dateStr] = { date: dateStr, Notifications: 0, Banners: 0 };
      }
      if (b.showAsBanner) dailyMap[dateStr].Banners++;
      else dailyMap[dateStr].Notifications++;
    });
    return Object.values(dailyMap).reverse();
  }, [broadcasts]);

  return (
    <div style={{ ...styles.page, padding: isMobile ? "16px" : "30px" }}>
      <h1 style={{ ...styles.title, fontSize: isMobile ? "20px" : "26px" }}>
        Broadcast & Banner Analytics
      </h1>

      {/* TOP SECTION: Form + Active Banners (Responsive 1-col or 2-col) */}
      <div style={isMobile ? styles.grid1Col : styles.grid2Col}>
        {/* Broadcast Form */}
        <div style={styles.card}>
          <h2 style={styles.cardHeader}>Send Broadcast</h2>
          <div style={styles.tabs}>
            <button
              style={{ ...styles.tabBtn, ...(audience === "customers" ? styles.tabActive : {}) }}
              onClick={() => setAudience("customers")}
            >
              📱 Customer
            </button>
            <button
              style={{ ...styles.tabBtn, ...(audience === "drivers" ? styles.tabActive : {}) }}
              onClick={() => setAudience("drivers")}
            >
              🚚 Driver
            </button>
            <button
              style={{ ...styles.tabBtn, ...(audience === "both" ? styles.tabActive : {}) }}
              onClick={() => setAudience("both")}
            >
              👥 Both
            </button>
          </div>

          <label style={styles.label}>Title</label>
          <input
            style={styles.input}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Scheduled maintenance"
            maxLength={100}
          />

          <label style={styles.label}>Message</label>
          <textarea
            style={styles.textarea}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write message..."
            rows={4}
            maxLength={300}
          />

          <label style={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={showAsBanner}
              onChange={(e) => setShowAsBanner(e.target.checked)}
            />
            Show as persistent banner
          </label>

          {showAsBanner && (
            <>
              <label style={styles.label}>Banner Type</label>
              <select
                style={styles.input}
                value={bannerType}
                onChange={(e) => setBannerType(e.target.value)}
              >
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="success">Success</option>
                <option value="error">Error</option>
              </select>
            </>
          )}

          <button style={styles.sendBtn} onClick={handleSend} disabled={sending}>
            {sending ? "Sending..." : `Send to ${audience.toUpperCase()}`}
          </button>
        </div>

        {/* Active Banners Display */}
        <div style={styles.card}>
          <h2 style={styles.cardHeader}>Active Persistent Banners</h2>
          {activeBanners.filter((b) => b.isActive).length === 0 ? (
            <p style={{ color: "#64748b", fontSize: "14px" }}>No active banners right now.</p>
          ) : (
            activeBanners
              .filter((b) => b.isActive)
              .map((b) => (
                <div key={b.audience} style={styles.bannerRow}>
                  <div>
                    <span style={styles.badge}>{b.audience.toUpperCase()}</span>
                    <strong style={{ marginLeft: "8px" }}>{b.title}</strong>
                    <div style={{ fontSize: "13px", color: "#64748b", marginTop: "4px" }}>
                      {b.message}
                    </div>
                  </div>
                  <button
                    style={styles.deactivateBtn}
                    onClick={() => handleDeactivate(b.audience)}
                  >
                    Deactivate
                  </button>
                </div>
              ))
          )}
        </div>
      </div>

      {/* FILTER BAR (Responsive Flex) */}
      <div style={styles.filterCard}>
        <h3 style={{ margin: "0 0 12px 0", fontSize: "16px", color: "#0f172a" }}>
          Filter Analytics & History
        </h3>
        <div style={isMobile ? styles.filterControlsMobile : styles.filterControls}>
          <div style={isMobile ? { width: "100%" } : {}}>
            <label style={styles.label}>Start Date</label>
            <input
              type="date"
              style={styles.inputFilter}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div style={isMobile ? { width: "100%" } : {}}>
            <label style={styles.label}>End Date</label>
            <input
              type="date"
              style={styles.inputFilter}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div style={isMobile ? { width: "100%" } : {}}>
            <label style={styles.label}>Type Filter</label>
            <select
              style={styles.inputFilter}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="notification_only">Notification Only</option>
              <option value="banner_and_notification">Banner + Notification</option>
            </select>
          </div>
          {(startDate || endDate || typeFilter !== "all") && (
            <button
              style={{ ...styles.resetBtn, width: isMobile ? "100%" : "auto" }}
              onClick={() => {
                setStartDate("");
                setEndDate("");
                setTypeFilter("all");
              }}
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      {/* KPI METRICS GRID */}
      <div style={styles.metricsGrid}>
        <div style={styles.metricCard}>
          <span style={styles.metricValue}>{metrics.totalBroadcasts}</span>
          <span style={styles.metricLabel}>Total Broadcasts</span>
        </div>
        <div style={styles.metricCard}>
          <span style={styles.metricValue}>{metrics.totalSent}</span>
          <span style={styles.metricLabel}>Total Push Sent</span>
        </div>
        <div style={{ ...styles.metricCard, borderLeft: "4px solid #ef4444" }}>
          <span style={{ ...styles.metricValue, color: "#ef4444" }}>{metrics.totalFailed}</span>
          <span style={styles.metricLabel}>Push Failures</span>
        </div>
        <div style={{ ...styles.metricCard, borderLeft: "4px solid #6366f1" }}>
          <span style={styles.metricValue}>{metrics.notificationOnlyCount}</span>
          <span style={styles.metricLabel}>Notification Only</span>
        </div>
        <div style={{ ...styles.metricCard, borderLeft: "4px solid #f59e0b" }}>
          <span style={styles.metricValue}>{metrics.bannerAndNotificationCount}</span>
          <span style={styles.metricLabel}>Banner + Notification</span>
        </div>
      </div>

      {/* CHARTS GRID */}
      <div style={styles.chartsGrid}>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Daily Broadcast Trend</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="Notifications" stroke="#6366f1" strokeWidth={2} />
              <Line type="monotone" dataKey="Banners" stroke="#f59e0b" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Broadcast Types Breakdown</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                outerRadius={70}
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}`}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Target Audience Volume</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={audienceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="audience" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* TABLE SECTION WITH HORIZONTAL SCROLL FOR MOBILE */}
      <div style={styles.cardFull}>
        <h2 style={styles.cardHeader}>Broadcast History</h2>
        {loadingHistory ? (
          <p>Loading history...</p>
        ) : broadcasts.length === 0 ? (
          <p style={{ color: "#64748b" }}>No broadcast records found for selected filters.</p>
        ) : (
          <div style={styles.tableResponsiveWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date & Time</th>
                  <th style={styles.th}>Audience</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Title</th>
                  <th style={styles.th}>Message</th>
                  <th style={styles.th}>Sent / Failed</th>
                  <th style={styles.th}>Sent By</th>
                </tr>
              </thead>
              <tbody>
                {broadcasts.map((b) => (
                  <tr key={b._id} style={styles.tr}>
                    <td style={styles.td}>{new Date(b.createdAt).toLocaleString()}</td>
                    <td style={styles.td}>
                      <span style={styles.badge}>{b.audience.toUpperCase()}</span>
                    </td>
                    <td style={styles.td}>
                      {b.showAsBanner ? (
                        <span style={{ ...styles.badge, background: "#fef3c7", color: "#92400e" }}>
                          Banner + Push
                        </span>
                      ) : (
                        <span style={{ ...styles.badge, background: "#e0e7ff", color: "#3730a3" }}>
                          Push Only
                        </span>
                      )}
                    </td>
                    <td style={{ ...styles.td, fontWeight: "600" }}>{b.title}</td>
                    <td style={{ ...styles.td, maxWidth: "200px" }}>{b.message}</td>
                    <td style={styles.td}>
                      <span style={{ color: "#16a34a", fontWeight: "600" }}>{b.sentCount}</span> /{" "}
                      <span style={{ color: "#dc2626", fontWeight: "600" }}>{b.failedCount}</span>
                    </td>
                    <td style={styles.td}>{b.sentBy?.name || b.sentBy?.email || "Admin"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  title: { margin: "0 0 20px 0", fontWeight: "800", color: "#0f172a" },
  grid2Col: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" },
  grid1Col: { display: "grid", gridTemplateColumns: "1fr", gap: "20px", marginBottom: "20px" },
  card: { background: "#fff", borderRadius: "16px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" },
  cardFull: { background: "#fff", borderRadius: "16px", padding: "20px", marginTop: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" },
  cardHeader: { margin: "0 0 16px 0", fontSize: "18px", color: "#0f172a" },
  tabs: { display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" },
  tabBtn: { flex: "1", padding: "8px 12px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", fontWeight: "600", fontSize: "13px", color: "#475569" },
  tabActive: { background: "#6366f1", color: "#fff", border: "1px solid #6366f1" },
  label: { display: "block", fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "4px" },
  input: { width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px", marginBottom: "12px", boxSizing: "border-box" },
  textarea: { width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px", marginBottom: "12px", boxSizing: "border-box", resize: "vertical" },
  checkboxRow: { display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#334155", marginBottom: "12px" },
  sendBtn: { width: "100%", padding: "12px", borderRadius: "10px", border: "none", background: "#6366f1", color: "#fff", fontWeight: "700", fontSize: "14px", cursor: "pointer" },
  bannerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f1f5f9", padding: "12px", borderRadius: "10px", marginBottom: "8px" },
  deactivateBtn: { background: "#fee2e2", color: "#991b1b", border: "none", padding: "6px 12px", borderRadius: "6px", fontSize: "12px", fontWeight: "700", cursor: "pointer" },
  filterCard: { background: "#fff", padding: "20px", borderRadius: "16px", marginBottom: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" },
  filterControls: { display: "flex", gap: "16px", alignItems: "flex-end", flexWrap: "wrap" },
  filterControlsMobile: { display: "flex", flexDirection: "column", gap: "12px" },
  inputFilter: { width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", boxSizing: "border-box" },
  resetBtn: { padding: "9px 14px", borderRadius: "6px", border: "none", background: "#e2e8f0", color: "#334155", cursor: "pointer", fontWeight: "600", fontSize: "13px" },
  metricsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "12px", marginBottom: "20px" },
  metricCard: { background: "#fff", padding: "16px", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", display: "flex", flexDirection: "column" },
  metricValue: { fontSize: "22px", fontWeight: "800", color: "#0f172a" },
  metricLabel: { fontSize: "12px", color: "#64748b", marginTop: "4px" },
  chartsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" },
  chartCard: { background: "#fff", padding: "16px", borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" },
  chartTitle: { fontSize: "14px", color: "#334155", marginBottom: "12px" },
  tableResponsiveWrapper: { overflowX: "auto", WebkitOverflowScrolling: "touch" },
  table: { width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "13px", minWidth: "600px" },
  th: { padding: "12px", borderBottom: "2px solid #e2e8f0", color: "#475569", whiteSpace: "nowrap" },
  tr: { borderBottom: "1px solid #f1f5f9" },
  td: { padding: "12px", color: "#334155", whiteSpace: "nowrap" },
  badge: { padding: "3px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: "700", background: "#f1f5f9", color: "#475569" },
};

export default BroadcastNotification;